import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

/**
 * Shared presentational primitives.
 *
 * These wrap the class names defined in index.css so screens compose elements
 * rather than repeating markup, and a style change lands in one file.
 */

export function Card({
  title,
  actions,
  children,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="card">
      {(title || actions) && (
        <div className="card-header">
          <span>{title}</span>
          {actions}
        </div>
      )}
      {children}
    </div>
  );
}

export function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'accent';

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

export function Alert({
  tone = 'error',
  children,
}: {
  tone?: 'error' | 'success' | 'warning';
  children: ReactNode;
}) {
  return <div className={`alert alert-${tone}`}>{children}</div>;
}

export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && (
        <span className="muted" style={{ fontSize: 12.5 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return <div className="empty">{label}</div>;
}

/**
 * Modal. Closes on Escape and on backdrop click — the click handler checks the
 * event target so a drag that ends outside the dialog doesn't dismiss it.
 */
export function Modal({
  title,
  onClose,
  footer,
  children,
}: {
  title: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="card-header">
          <span>{title}</span>
          <button className="btn btn-sm" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="card-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * Copy-to-clipboard button. Falls back silently when the Clipboard API is
 * unavailable (non-secure origins) rather than throwing at the user.
 */
/**
 * Inline click-to-copy. Renders `display` (or the value) as a clickable,
 * monospace token; clicking copies the FULL value and flashes "copied". Use
 * for addresses, tx hashes, account numbers in tables.
 */
export function Copyable({
  value,
  display,
  href,
}: {
  value: string | null | undefined;
  display?: string;
  href?: string;
}) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="muted">—</span>;

  const shown = display ?? value;
  const copy = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    e.preventDefault();
    e.stopPropagation();
    void navigator.clipboard?.writeText(value).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        className="mono"
        onClick={copy}
        title={`Click to copy: ${value}`}
        style={{ cursor: 'pointer', textDecoration: copied ? 'none' : undefined }}
      >
        {copied ? '✓ copied' : shown}
      </span>
      {href && !copied && (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          title="Open in explorer"
          style={{ fontSize: 12, textDecoration: 'none' }}
        >
          ↗
        </a>
      )}
    </span>
  );
}

export function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
  return (
    <button
      className="btn btn-sm"
      onClick={() => {
        void navigator.clipboard?.writeText(value).catch(() => undefined);
      }}
    >
      {label}
    </button>
  );
}
